import { Injectable } from '@angular/core';
import { AuthorizationService } from '@services/authorization.service';
import { AuthenticationRepository } from '@repository/authentication/authentication.repository';
import { AuthenticationService } from '@services/authentication.service';
import { Observable, map, mergeMap, take, timeout } from 'rxjs';
import { ConnectionError, SSOError } from '@repository/authentication/models/errors.model';
import { TenantSSOOptionsService } from '@services/tenant-sso-options.service';
import { LoginResponse as OidcLoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { SpinnerService } from '@services/spinner.service';
import { Router } from '@angular/router';
import { SSOLogoutService } from '@services/sso-logout.service';
import { finalize } from 'rxjs/operators';
import { SSOProvider, TenantSSOOptions } from '../../../../types/login/SSO';
import { LoginResponse } from '../../../../types/DataResult';
import { AuthErrorHandlerService } from './auth-error-handler.service';

@Injectable()
export class SSOLoginBusinessService {
  private static readonly TIMEOUT_MS = 5000;
  private static readonly DEFAULT_REDIRECT_URL = '/';
  public readonly tenantSSOOptions$: Observable<TenantSSOOptions>;
  public constructor(
    private readonly authorizationService: AuthorizationService,
    private readonly authenticationRepository: AuthenticationRepository,
    private readonly tenantSSOOptionsService: TenantSSOOptionsService,
    private readonly authenticationService: AuthenticationService,
    private readonly oidc: OidcSecurityService,
    private readonly spinnerService: SpinnerService,
    private readonly router: Router,
    private readonly authErrorHandlerService: AuthErrorHandlerService,
    private readonly ssoLogoutService: SSOLogoutService,
  ) {
    this.tenantSSOOptions$ = this.tenantSSOOptionsService.tenantSSOOptions$;
  }

  private ssoLogin(idToken: string): Observable<LoginResponse> {
    this.authorizationService.cleanUserAndUserAuthorization();
    return this.authenticationRepository.ssoLogin(idToken).pipe(timeout(SSOLoginBusinessService.TIMEOUT_MS));
  }

  public handleIdpInitiatedCallback(code: string): void {
    this.authorizationService.cleanUserAndUserAuthorization();
    this.authenticationRepository
      .idpInitiatedLogin(code)
      .pipe(timeout(SSOLoginBusinessService.TIMEOUT_MS))
      .subscribe({
        next: (result) => {
          this.authenticationService.loginSucceeded(result.token, result.payload);
          void this.router.navigateByUrl(SSOLoginBusinessService.DEFAULT_REDIRECT_URL);
        },
        error: (error) => {
          this.authErrorHandlerService.present(error);
          void this.router.navigate(['/auth/login']);
        },
      });
  }

  public handleAuthorizationCallback(configId: SSOProvider | undefined): void {
    if (!configId) {
      this.authErrorHandlerService.present(new SSOError({ cause: new Error('Unknown SSO provider') }));
      void this.router.navigate(['/auth/login']);
      return;
    }
    this.checkAuthOnce(configId)
      .pipe(mergeMap((loginResponse) => this.ssoLogin(loginResponse.idToken)))
      .subscribe({
        next: (result) => {
          this.authenticationService.loginSucceeded(result.token, result.payload);
          void this.router.navigateByUrl(SSOLoginBusinessService.DEFAULT_REDIRECT_URL);
        },
        error: (error) => {
          this.authErrorHandlerService.present(error);
          this.ssoLogoutService.logout();
          void this.router.navigate(['/auth/login']);
        },
      });
  }

  private checkAuthOnce(configId: SSOProvider): Observable<OidcLoginResponse> {
    return this.oidc.checkAuth(undefined, configId).pipe(
      take(1),
      map((result) => {
        if (!result.isAuthenticated) {
          throw new SSOError({ cause: new Error('Not authenticated after callback') });
        }
        return result;
      }),
    );
  }

  public loginWithSSO(): void {
    this.spinnerService.show();

    this.tenantSSOOptions$
      .pipe(
        take(1),
        finalize(() => this.spinnerService.hide()),
      )
      .subscribe({
        next: (options) => {
          try {
            if (options.provider !== SSOProvider.COGNITO) {
              void this.router.navigate(['/auth/login']);
              throw new Error('No SSO provider configured for this tenant');
            }
            this.oidc.authorize(SSOProvider.COGNITO);
          } catch (error) {
            const errorWrapper = new SSOError({
              cause: error instanceof Error ? error : new Error(String(error)),
            });
            this.authErrorHandlerService.present(errorWrapper);
          }
        },
        error: (error) => {
          const errorWrapper = new ConnectionError({ cause: error });
          this.authErrorHandlerService.present(errorWrapper);
        },
      });
  }
}
